/**
 * Bloomable location + featured awareness for the New Theme predictive search.
 *
 * This does NOT replace Canopy's <predictive-search> web component — it layers
 * onto it. Canopy keeps owning product results, debouncing, keyboard nav, a11y
 * and overlay handling. We only add the Bloomable-specific bits that the old
 * theme's search.js provided:
 *
 *   • A "Featured" group  — collections whose title matches the query
 *     (from snippets/bloomable-search-featured-list.liquid, embedded in
 *     #bloomable-search-data).
 *   • A "Locations" group — country/province/city/town collections matched
 *     instantly from the lazy-loaded top-locations dataset, plus suburb-level
 *     location collections discovered via /search/suggest.json.
 *   • Smart redirect on submit — if the typed query exactly matches a known
 *     location title, go straight to /collections/<handle> instead of /search.
 *
 * Data sources (already rendered by the New Theme):
 *   • #bloomable-search-data            — theme.liquid (suburb handles, featured,
 *                                          non-location handles, version, prefix).
 *   • bloomable-search-top-locations    — lazy section, fetched on first focus.
 */
(function () {
  'use strict';

  var SUGGEST_LIMIT = 6;
  var MAX_LOCATIONS_SHOWN = 6;
  var MAX_FEATURED_SHOWN = 2;

  // ─── Bootstrap dataset ──────────────────────────────────────────────────────

  var dataEl = document.getElementById('bloomable-search-data');
  var topLocations = [];
  var suburbSet = new Set();
  var nonLocationSet = new Set();
  var featuredCollections = [];
  var topLocationsVersion = 0;
  var locationHandlePrefix = '';

  if (dataEl) {
    try {
      var data = JSON.parse(dataEl.textContent || '{}');
      suburbSet = new Set(Array.isArray(data.suburbHandles) ? data.suburbHandles : []);
      nonLocationSet = new Set(Array.isArray(data.nonLocationHandles) ? data.nonLocationHandles : []);
      featuredCollections = Array.isArray(data.featuredCollections) ? data.featuredCollections : [];
      topLocationsVersion = data.topLocationsVersion || 0;
      locationHandlePrefix = data.locationHandlePrefix || '';
    } catch (e) {
      console.warn('[blm-search] failed to parse #bloomable-search-data', e);
    }
  } else {
    console.warn('[blm-search] #bloomable-search-data not found — locations/featured disabled');
  }

  // Warm topLocations from a versioned localStorage cache so the first keystroke
  // can match without waiting on the network.
  var TOP_LOCATIONS_CACHE_KEY = 'blm-search-top-locations-v1';
  try {
    var cached = JSON.parse(localStorage.getItem(TOP_LOCATIONS_CACHE_KEY) || 'null');
    if (cached && cached.version === topLocationsVersion && Array.isArray(cached.data)) {
      topLocations = cached.data;
    }
  } catch (e) { /* ignore */ }

  function indexLocations() {
    topLocations.forEach(function (l) { l._lc = (l.title || '').toLowerCase(); });
  }
  indexLocations();
  featuredCollections.forEach(function (f) { f._loose = looseKey(f.title); });

  // Lazy-load the (larger) top-locations dataset via the Section Rendering API.
  var topLocationsPromise = null;
  function ensureTopLocations() {
    if (topLocations.length > 0) return Promise.resolve(topLocations);
    if (topLocationsPromise) return topLocationsPromise;
    topLocationsPromise = fetch('/?sections=bloomable-search-top-locations')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = sections['bloomable-search-top-locations'] || '';
        var match = html.match(/<script[^>]*id="bloomable-search-top-locations-data"[^>]*>([\s\S]*?)<\/script>/);
        if (!match) throw new Error('top-locations data script not found');
        var parsed = JSON.parse(match[1]);
        topLocations = Array.isArray(parsed) ? parsed : [];
        indexLocations();
        try {
          localStorage.setItem(TOP_LOCATIONS_CACHE_KEY, JSON.stringify({ version: topLocationsVersion, data: parsed }));
        } catch (e) { /* quota / disabled — ignore */ }
        return topLocations;
      })
      .catch(function (err) {
        console.warn('[blm-search] failed to lazy-load topLocations', err);
        topLocationsPromise = null; // allow retry
        return [];
      });
    return topLocationsPromise;
  }

  // Kick the lazy load on first focus of any predictive-search input.
  var topLocationsKicked = false;
  document.addEventListener('focusin', function (e) {
    if (topLocationsKicked) return;
    if (e.target && e.target.matches && e.target.matches('.js-search-input')) {
      topLocationsKicked = true;
      ensureTopLocations();
    }
  }, true);

  // ─── Matching ────────────────────────────────────────────────────────────────

  function normalise(s) {
    return String(s == null ? '' : s).toLowerCase()
      .replace(/[‘’‚‛′‵`']/g, "'")
      .replace(/[“”„‟″‶"]/g, '"')
      .replace(/\s+/g, ' ').trim();
  }

  // Loose key for fuzzy matching: drop apostrophes (so "mothers" == "Mother's")
  // and turn any other punctuation / "&" into spaces, then collapse whitespace.
  // "Mother's Day Gifts & Flowers" → "mothers day gifts flowers".
  function looseKey(s) {
    return normalise(s)
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9 ]+/g, ' ')
      .replace(/\s+/g, ' ').trim();
  }

  // A featured collection matches when every word of the query appears in its
  // (loose) title. Ranked so titles starting with the query come first.
  function matchFeatured(query) {
    var q = looseKey(query);
    if (!q) return [];
    var tokens = q.split(' ').filter(Boolean);
    return featuredCollections
      .map(function (f) {
        var hay = f._loose;
        var allPresent = tokens.every(function (t) { return hay.indexOf(t) !== -1; });
        if (!allPresent) return null;
        // Rank: whole-query prefix wins, then whole-query substring, then token-only.
        var rank = hay.indexOf(q) === 0 ? 0 : (hay.indexOf(q) !== -1 ? 1 : 2);
        return { f: f, rank: rank };
      })
      .filter(Boolean)
      .sort(function (a, b) { return a.rank - b.rank; })
      .map(function (m) { return m.f; });
  }

  function matchLocations(query) {
    var q = query.toLowerCase().trim();
    if (!q) return [];
    var typeRank = { country: 0, province: 1, city: 2, town: 2, suburb: 3 };
    return topLocations
      .map(function (l) {
        var idx = l._lc.indexOf(q);
        if (idx === -1) return null;
        var prefixBonus = l._lc.indexOf(q) === 0 ? 0 : 1;
        var wordStartBonus = (' ' + l._lc).indexOf(' ' + q) !== -1 ? 0 : 1;
        var rank = typeRank[l.type] != null ? typeRank[l.type] : 9;
        var score = (prefixBonus * 1000) + (wordStartBonus * 500) + idx * 10 + rank;
        return { loc: l, score: score };
      })
      .filter(Boolean)
      .sort(function (a, b) { return a.score - b.score; })
      .slice(0, MAX_LOCATIONS_SHOWN)
      .map(function (s) { return s.loc; });
  }

  function exactLocationMatch(query) {
    var q = query.toLowerCase().trim();
    if (!q) return null;
    return topLocations.find(function (l) { return l._lc === q; }) || null;
  }

  // Discover suburb / prefixed location collections from Shopify's suggest API,
  // skipping featured (shown separately) and known product collections.
  function fetchSuggestLocations(query, signal) {
    var params = new URLSearchParams({
      q: query,
      'resources[type]': 'collection',
      'resources[limit]': String(SUGGEST_LIMIT),
      'resources[options][fields]': 'title'
    });
    return fetch('/search/suggest.json?' + params, { signal: signal, headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('suggest http ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var results = (data && data.resources && data.resources.results) || {};
        var cols = results.collections || [];
        var featuredHandles = new Set(featuredCollections.map(function (f) { return f.handle; }));
        var byHandle = {};
        topLocations.forEach(function (l) { byHandle[l.handle] = l; });
        var out = [];
        cols.forEach(function (c) {
          if (featuredHandles.has(c.handle)) return;
          if (nonLocationSet.has(c.handle)) return;
          var hasPrefix = locationHandlePrefix && c.handle.indexOf(locationHandlePrefix) === 0;
          if (!hasPrefix && !suburbSet.has(c.handle)) return; // not a location
          var known = byHandle[c.handle];
          if (known) {
            out.push({ handle: known.handle, title: known.title, type: known.type || 'location', parentTitle: known.parentTitle });
          } else {
            out.push({ handle: c.handle, title: c.title, type: 'suburb' });
          }
        });
        return out;
      });
  }

  // ─── Rendering ────────────────────────────────────────────────────────────────

  // Reuse Canopy's own result-row classes (.predictive-result, __media, __info,
  // __title h6, __sub-title) so featured/location rows render identically to the
  // product rows — same font, image size, padding, and hover. Our only custom
  // bits are the right-aligned badge/tag.
  function featuredItemHtml(f) {
    var media = f.image
      ? '<div class="predictive-result__media media relative"><img class="predictive-result__media" src="' + escapeAttr(f.image) + '" alt="" loading="lazy"></div>'
      : '<div class="predictive-result__media media relative"></div>';
    return '<li class="predictive-search__item blm-s__item" role="option">'
      + '<a class="predictive-result flex items-start focus-inset" href="/collections/' + encodeURIComponent(f.handle) + '">'
      + media
      + '<div class="predictive-result__info flex-auto">'
      + '<h3 class="predictive-result__title h6 font-body regular-text">' + escapeHtml(f.title) + '</h3>'
      + '</div>'
      + '<span class="blm-s__badge text-xs">Featured</span>'
      + '</a></li>';
  }

  function locationItemHtml(l) {
    var typeLabel = (l.type || 'location').toUpperCase();
    var tagType = escapeAttr((l.type || 'location').toLowerCase());
    var sub = l.parentTitle
      ? '<div class="predictive-result__sub-title text-sm">in ' + escapeHtml(l.parentTitle) + '</div>'
      : '';
    var pin = '<div class="predictive-result__media media relative blm-s__pin">'
      + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21s-6-5.3-6-10a6 6 0 0 1 12 0c0 4.7-6 10-6 10z"/><circle cx="12" cy="11" r="2"/></svg>'
      + '</div>';
    return '<li class="predictive-search__item blm-s__item" role="option">'
      + '<a class="predictive-result flex items-start focus-inset" href="/collections/' + encodeURIComponent(l.handle) + '">'
      + pin
      + '<div class="predictive-result__info flex-auto">'
      + '<h3 class="predictive-result__title h6 font-body regular-text">' + escapeHtml(l.title) + '</h3>'
      + sub
      + '</div>'
      + '<span class="blm-s__tag blm-s__tag--' + tagType + ' text-xs">' + escapeHtml(typeLabel) + '</span>'
      + '</a></li>';
  }

  function groupHtml(title, itemsHtml, seeMoreHref) {
    var seeMore = seeMoreHref
      ? '<a class="blm-s__see-more text-sm" href="' + escapeAttr(seeMoreHref) + '">See more &rarr;</a>'
      : '';
    // Mirror Canopy's own predictive "Suggestions" heading recipe exactly:
    // h6 + regular-text (resets to 1em) + font-body + font-bold.
    return '<div class="blm-s__section">'
      + '<div class="blm-s__heading"><span class="blm-s__heading-name h6 regular-text font-body font-bold">' + escapeHtml(title) + '</span>' + seeMore + '</div>'
      + '<ul class="predictive-search__list blm-s__list" role="list">' + itemsHtml + '</ul>'
      + '</div>';
  }

  // Build (or rebuild) the Bloomable groups at the top of Canopy's results card.
  // The markup Canopy injects into .js-search-results is:
  //   .predictive-search (the dropdown card, display governed by [open])
  //     └─ .predictive-search__scroller (overflow-auto)
  //          └─ .predictive-search__results (suggestions | tabs)
  // We insert into the scroller so our groups live INSIDE the styled, visibility-
  // controlled card, full width, above the suggestions/products. Falling back to
  // the card itself — but never to the bare host, which would render outside the
  // card with no [open] visibility control.
  function renderGroups(host, query, featured, locations) {
    var target = host.querySelector('.predictive-search__scroller')
      || host.querySelector('.predictive-search');
    if (!target) return; // no card rendered yet — nothing to attach to

    var existing = target.querySelector('.blm-s-extra');
    if (existing) existing.remove();
    if (!featured.length && !locations.length) return;

    var wrap = document.createElement('div');
    wrap.className = 'blm-s-extra';

    var html = '';
    if (featured.length) {
      html += groupHtml('Featured', featured.map(featuredItemHtml).join(''), null);
    }
    if (locations.length) {
      var seeMore = '/search?q=' + encodeURIComponent(query) + '&type=collection';
      html += groupHtml('Locations', locations.map(locationItemHtml).join(''), seeMore);
    }
    wrap.innerHTML = html;

    // Sit at the top of the card, above suggestions and product tabs.
    target.insertBefore(wrap, target.firstChild);
  }

  // ─── Hook the web component ─────────────────────────────────────────────────

  var suggestController = null;

  function patch(PredictiveSearch) {
    var proto = PredictiveSearch.prototype;
    if (proto.__blmPatched) return;
    proto.__blmPatched = true;

    // renderResults is dispatched dynamically (this.renderResults(...)), so a
    // prototype wrap takes effect on every instance.
    var origRender = proto.renderResults;

    proto.renderResults = function (markup) {
      // Let Canopy inject product markup and open the panel first.
      origRender.call(this, markup);

      var self = this;
      var query = this.getQuery();
      if (!query) return;

      var token = (this.__blmToken || 0) + 1;
      this.__blmToken = token;

      var featured = matchFeatured(query).slice(0, MAX_FEATURED_SHOWN);

      var renderWith = function (locations) {
        if (self.__blmToken !== token) return; // a newer query superseded this one
        renderGroups(self.results, query, featured, locations.slice(0, MAX_LOCATIONS_SHOWN));
      };

      // Instant pass from local data.
      var localLocations = matchLocations(query);
      renderWith(localLocations);

      // Make sure the lazy dataset is loaded, then re-match locally.
      ensureTopLocations().then(function () {
        if (self.__blmToken !== token) return;
        localLocations = matchLocations(query);
        renderWith(localLocations);
      });

      // Async pass: discover suburb-level location collections, merge & dedupe.
      if (suggestController) suggestController.abort();
      suggestController = new AbortController();
      fetchSuggestLocations(query, suggestController.signal)
        .then(function (suggestLocations) {
          if (self.__blmToken !== token) return;
          var seen = {};
          var merged = [];
          matchLocations(query).concat(suggestLocations).forEach(function (l) {
            if (seen[l.handle]) return;
            seen[l.handle] = true;
            merged.push(l);
          });
          renderWith(merged);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          console.warn('[blm-search] suggest locations failed', err);
        });
    };
  }

  // Smart redirect on submit. Canopy binds its own submit handler in the
  // constructor, so we intercept in the capture phase (runs first) and only act
  // on an exact location match — otherwise Canopy's handler and the native
  // submit proceed untouched.
  document.addEventListener('submit', function (evt) {
    var form = evt.target;
    if (!form || !form.matches || !form.matches('form.search')) return;
    if (!form.closest('predictive-search')) return;
    var input = form.querySelector('.js-search-input');
    if (!input) return;
    var exact = exactLocationMatch(input.value.trim());
    if (exact) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      window.location.href = '/collections/' + exact.handle;
    }
  }, true);

  function waitForComponent() {
    if (window.customElements && customElements.get('predictive-search')) {
      patch(customElements.get('predictive-search'));
      return;
    }
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('predictive-search').then(function () {
        patch(customElements.get('predictive-search'));
      });
    }
  }
  waitForComponent();

  // ─── Helpers ───────────────────────────────────────────────────────────────

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(s) { return escapeHtml(s); }
})();
